import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import styles from './css/index.module.scss';
import { DICTIONARY } from '../../translations/dictionary';
import useTranslations from '../../hooks/useTranslations';
import Checkbox from '../../ui-kit/Checkbox';
import AddGrayIcon from '../../assets/icons/addGray.svg';
import DeleteGrayIcon from '../../assets/icons/deleteGray.svg';
import DownloadGrayIcon from '../../assets/icons/downloadGray.svg';
import EmailGrayIcon from '../../assets/icons/emailGray.svg';
import Pagination from '../../ui-kit/Pagination';
import Button from '../../ui-kit/Button';
import useConfigurations from '../../hooks/useConfigurations';
import { initVersionAtom, isInitAtom, scrollableContentRefAtom } from '../../atoms/globalAtoms';
import useGridLayout from '../../hooks/useGridLayout';
import { closeModalAtom, openConfirmModalAtom, openInfoModalAtom } from '../../atoms/modalAtoms';
import ProjectsNavigationBlocks from '../MyProjects/components/ProjectsNavigationBlocks';
import ProjectsSelectingButtons from '../MyProjects/components/ProjectsSelectingButtons';
import CircleData from '../../ui-kit/CircleData';
import DocumentGrayIcon from '../../assets/icons/documentGray.svg';
import ConfiguratorServices from '../../services/ConfiguratorServices';
import CalculationDetailsServices from '../../services/CalculationDetailsServices';
import EmptyHeight from '../../components/EmptyHeight';
import Loading from '../../ui-kit/Loading';
import SimpleHeader from '../../ui-kit/SimpleHeader';
import ProjectName from './components/ProjectName';
import { configuratorCalculationsAtom, configuratorSingleConfigurationAtom } from '../../atoms/configuratorAtoms';
import getLocalizedText from '../../helpers/getLocalizedText';
import { projectsSearchQueryAtom } from '../../atoms/projectsAtoms';
import { configurationsSearchQueryAtom } from '../../atoms/configurationsAtoms';

// Must match the desktop .listItem height in css/index.module.scss.
const CONFIGURATION_ITEM_HEIGHT = 114;

function Configurations({ withProject }) {
  const { t, lang } = useTranslations();
  const navigate = useNavigate();
  const [isInit] = useAtom(isInitAtom);
  const [initVersion] = useAtom(initVersionAtom);
  const [scrollableContentRef] = useAtom(scrollableContentRefAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [, setConfiguratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);
  const [, setConfiguratorCalculations] = useAtom(configuratorCalculationsAtom);

  const [selectedItems, setSelectedItems] = useState([]);

  const [isInitLocal, setIsInitLocal] = useState(false);

  const {
    configurations,
    configurationsProject,
    gettingConfigurationsLoading,
    configurationsPagesInfo,
    configurationsSearchQuery: search,
    getConfigurations,
    getSingleConfiguration,
    downloadConfigurationsArchive,
    downloadConfigurationsFile,
    emailConfigurationsArchive,
    getConfigurationsOfProject,
    onAddToProjectClick,
    onDeleteConfigurationsClick,
    onDetachConfigurationsClick,
  } = useConfigurations({ setSelectedItems });

  const [, setConfigurationsSearchQuery] = useAtom(configurationsSearchQueryAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page');
  const projectId = withProject ? searchParams.get('project_id') : null;
  const isMobile = window.innerWidth <= 900;

  const listRef = useRef(null);
  const paginationRef = useRef(null);
  const listLayout = useGridLayout({
    gridRef: listRef,
    paginationRef,
    scrollableContentRef,
    isEnabled: !isMobile,
    itemHeight: CONFIGURATION_ITEM_HEIGHT,
    // On low screens the page scrolls rather than showing one or two configurations.
    minRows: 3,
  });
  // Desktop: as many rows as fit on the screen. Mobile keeps the server default and "show more".
  const itemsOnPage = isMobile ? null : listLayout?.rows ?? null;

  // The previous project stays in state until the next request finishes.
  const isCurrentProject = !withProject || String(configurationsProject?.id ?? '') === String(projectId ?? '');

  const pagesCount = useMemo(() => {
    if (!configurationsPagesInfo) return 1;
    return Math.ceil(configurationsPagesInfo.total_records / configurationsPagesInfo.items_on_page);
  }, [configurationsPagesInfo]);

  const configurationsToDisplay = useMemo(() => {
    if (isMobile) return configurations;
    return configurations.slice(0, configurationsPagesInfo?.items_on_page || 0);
  }, [configurations, isMobile, configurationsPagesInfo?.items_on_page]);

  const onShowMoreClick = () => {
    onPageChange(+page + 1);
  };

  const onSelectItem = (item) => {
    if (selectedItems.includes(item.id)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item.id));
    } else {
      setSelectedItems((prev) => [...prev, item.id]);
    }
  };

  const onPageChange = (val) => {
    const newParams = Object.fromEntries([...searchParams.entries()]);
    newParams.page = val;

    if (selectedItems.length && !isMobile) {
      openConfirmModal({
        text: t(DICTIONARY.changePageWarning),
        onButtonClick: () => {
          setSearchParams(newParams, { replace: true });
          setSelectedItems([]);
          closeModal();
        },
      });
    } else {
      setSearchParams(newParams, { replace: true });
    }
  };

  const onConfigurationClick = (item) => {
    setConfiguratorSingleConfiguration(null);
    setConfiguratorCalculations(null);
    navigate(`/configurator?configuration_id=${item.id}&only_details=true`);
  };

  useEffect(() => () => setConfigurationsSearchQuery(''), []);

  const searchRef = useRef(search);
  const initVersionRef = useRef(initVersion);
  const itemsOnPageRef = useRef(itemsOnPage);

  const requestPage = (requestedPage, shouldAddToExisting) => (withProject
    ? getConfigurationsOfProject({
      page: requestedPage, projectId, shouldAddToExisting, itemsOnPage,
    })
    : getConfigurations({
      page: requestedPage, search, shouldAddToExisting, itemsOnPage,
    }));

  // Keeps project_id and other params of the address.
  const replacePage = (nextPage) => {
    const newParams = Object.fromEntries([...searchParams.entries()]);
    newParams.page = nextPage;
    setSearchParams(newParams, { replace: true });
  };

  // Mobile shows pages 1..page one after another; all of them are requested again, in order.
  const reloadShownPages = async () => {
    if (!isMobile) {
      await requestPage(page, false);
      return;
    }
    for (let i = 1; i <= +page; i++) {
      // eslint-disable-next-line no-await-in-loop
      await requestPage(i, i > 1);
    }
  };

  useEffect(() => {
    if (!isInit) return;
    // On desktop the page size is known only after the list is measured.
    if (!isMobile && !itemsOnPage) return;

    const searchChanged = searchRef.current !== search;
    const initChanged = initVersionRef.current !== initVersion;
    const previousItemsOnPage = itemsOnPageRef.current;
    const itemsOnPageChanged = Boolean(previousItemsOnPage && itemsOnPage && previousItemsOnPage !== itemsOnPage);
    searchRef.current = search;
    initVersionRef.current = initVersion;
    itemsOnPageRef.current = itemsOnPage;

    if (searchChanged && page && page !== '1') {
      replacePage(1);
      return;
    }

    // The page size changed with the screen: open the page that holds the first configuration seen before.
    if (itemsOnPageChanged && !searchChanged && page) {
      const firstItemIndex = (+page - 1) * previousItemsOnPage;
      const nextPage = Math.floor(firstItemIndex / itemsOnPage) + 1;
      if (nextPage !== +page) {
        replacePage(nextPage);
        return;
      }
    }

    // Init ran again (the language changed): the same list is requested again to get it in the new language.
    if (initChanged && isInitLocal && page) {
      reloadShownPages().then();
      return;
    }

    if (page) {
      requestPage(page, isMobile).then();
      // A resize keeps the place on the screen.
      if (!isMobile && !itemsOnPageChanged) scrollableContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      onPageChange(1);
    }

    if (!isInitLocal) setIsInitLocal(true);
  }, [page, search, withProject, projectId, isInit, initVersion, itemsOnPage]);

  return (
    <div className={styles.configurations}>
      <ProjectsNavigationBlocks />
      {withProject && (
        <div className={styles.projectInfo}>
          <SimpleHeader
            subtitle={`${t(DICTIONARY.personalAccount)} / ${t(DICTIONARY.myProjects)}`}
            titleContent={isCurrentProject ? (
              <ProjectName project={configurationsProject} />
            ) : (
              <div className={styles.projectNameLoading}>
                <Loading height={22} width={22} />
              </div>
            )}
            className={styles.projectInfoHeader}
          />
        </div>
      )}
      <div className={styles.buttons}>
        <ProjectsSelectingButtons
          allItems={configurationsToDisplay}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          isForHistory={!withProject}
        />
      </div>

      <div className={styles.list} ref={listRef}>
        {(gettingConfigurationsLoading || !isInitLocal || (withProject && !isCurrentProject))
          ? <div className={styles.loading}><Loading height={30} width={30} /></div>
          : !configurationsToDisplay?.length
            ? <p className={styles.emptyText}>{search ? t(DICTIONARY.searchEmpty) : t(DICTIONARY.empty)}</p>
            : configurationsToDisplay.map((item) => (
              <div className={styles.listItem} key={item.id}>
                <div className={styles.checkbox}>
                  <Checkbox
                    onClick={() => onSelectItem(item)}
                    value={selectedItems.includes(item.id)}
                  />
                </div>
                <div
                  className={classNames(styles.header, 'pressable')}
                  onClick={() => onConfigurationClick(item)}
                >
                  <p className={styles.subtitle}>{getLocalizedText(item.city, lang)}</p>
                  <p className={styles.title} title={getLocalizedText(item.name, lang)}>{getLocalizedText(item.name, lang)}</p>
                  {item.created_at && moment(item.created_at).isValid() ? (
                    <p className={styles.created}>
                      {t(DICTIONARY.created, true)}
                      {' '}
                      {moment(item.created_at).format('DD.MM.YY')}
                    </p>
                  ) : null}
                </div>
                <div className={styles.circlesRow}>
                  {item.results?.map((i, index) => (
                    <CircleData
                      key={typeof i.value === 'object' ? index : i.value}
                      name={CalculationDetailsServices.formatNumber(getLocalizedText(i.value, lang))}
                      value={CalculationDetailsServices.renderCharacteristicName(getLocalizedText(i.name, lang))}
                    />
                  ))}
                </div>
                {/* Buttons Start */}
                <div className={styles.icons}>
                  <div
                    className={classNames('pressable')}
                    onClick={() => downloadConfigurationsArchive([item.id])}
                  >
                    <img src={DownloadGrayIcon} alt="Download" />
                  </div>
                  <div
                    className={classNames('pressable')}
                    onClick={() => downloadConfigurationsFile([item.id])}
                  >
                    <img src={DocumentGrayIcon} alt="Document" />
                  </div>
                  <div
                    className={classNames('pressable')}
                    onClick={() => emailConfigurationsArchive([item.id])}
                  >
                    <img src={EmailGrayIcon} alt="Email" />
                  </div>
                  {!withProject && (
                    <div
                      className={classNames('pressable')}
                      onClick={() => onAddToProjectClick(item)}
                    >
                      <img src={AddGrayIcon} alt="Add" />
                    </div>
                  )}
                  {!withProject || item.project_id ? (
                    <div
                      className={classNames('pressable')}
                      onClick={() => (withProject
                        ? onDetachConfigurationsClick(item.project_id, [item.id])
                        : onDeleteConfigurationsClick([item.id]))}
                    >
                      <img src={DeleteGrayIcon} alt="Delete" />
                    </div>
                  ) : <EmptyHeight height={18} width={18} />}

                </div>
                {/* Buttons End */}
              </div>
            ))}
      </div>

      {isMobile
        ? configurationsPagesInfo?.last_page > configurationsPagesInfo?.current_page ? (
          <div className={styles.showMoreButton}>
            <Button
              onClick={onShowMoreClick}
              text={t(DICTIONARY.showMore)}
              width="290px"
              isDisabled={gettingConfigurationsLoading}
            />
          </div>
        ) : <EmptyHeight height={20} /> : (
          <div ref={paginationRef}>
            <Pagination page={+page} setPage={onPageChange} pagesCount={pagesCount} />
          </div>
        )}
    </div>
  );
}

export default Configurations;
